"use client";

import { GUIDE_CATEGORY_FILTERS } from "@/lib/light-pages";
import type { ReactNode } from "react";

export type GuideSort = "recent" | "oldest";

type GuidesSidebarFiltersProps = {
  sort: GuideSort;
  onSortChange: (sort: GuideSort) => void;
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  categoryCounts: Record<string, number>;
  resultCount: number;
  compact?: boolean;
};

const SORT_OPTIONS: { value: GuideSort; label: string }[] = [
  { value: "recent", label: "Plus récents" },
  { value: "oldest", label: "Plus anciens" },
];

function FilterButton({
  active,
  onClick,
  children,
  count,
  compact = false,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  count?: number;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-3 rounded-xl text-left text-sm font-semibold transition-all ${
        compact ? "px-3 py-2" : "px-3.5 py-2.5"
      } ${
        active
          ? "bg-[#1C1C1C] text-white shadow-[0_8px_20px_-10px_rgba(0,0,0,0.4)]"
          : "text-zinc-600 hover:bg-zinc-50 hover:text-[#1C1C1C]"
      }`}
    >
      <span>{children}</span>
      {count !== undefined && (
        <span
          className={`inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[0.68rem] font-bold tabular-nums ${
            active ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-500"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function GuidesSidebarFilters({
  sort,
  onSortChange,
  selectedCategory,
  onCategoryChange,
  categoryCounts,
  resultCount,
  compact = false,
}: GuidesSidebarFiltersProps) {
  return (
    <div>
      <div className={compact ? "mb-4" : "mb-6"}>
        <h2 className={`font-bold uppercase tracking-[0.12em] text-zinc-400 ${compact ? "mb-2 text-[0.68rem]" : "mb-3 text-xs"}`}>
          Trier par
        </h2>
        <div className={compact ? "space-y-0.5" : "space-y-1"}>
          {SORT_OPTIONS.map((option) => (
            <FilterButton
              key={option.value}
              active={sort === option.value}
              onClick={() => onSortChange(option.value)}
              compact={compact}
            >
              {option.label}
            </FilterButton>
          ))}
        </div>
      </div>

      <div className={`border-t border-dashed border-zinc-200 ${compact ? "mb-3 pt-4" : "mb-5 pt-5"}`}>
        <h2 className={`font-bold uppercase tracking-[0.12em] text-zinc-400 ${compact ? "mb-2 text-[0.68rem]" : "mb-3 text-xs"}`}>
          Catégorie
        </h2>
        <div className={compact ? "space-y-0.5" : "space-y-1"}>
          {GUIDE_CATEGORY_FILTERS.map((category) => (
            <FilterButton
              key={category}
              active={selectedCategory === category}
              onClick={() => onCategoryChange(category)}
              count={categoryCounts[category] ?? 0}
              compact={compact}
            >
              {category}
            </FilterButton>
          ))}
        </div>
      </div>

      <p className={`border-t border-dashed border-zinc-200 font-medium text-zinc-400 ${compact ? "pt-3 text-xs" : "pt-4 text-xs sm:text-sm"}`}>
        {resultCount} guide{resultCount > 1 ? "s" : ""} affiché{resultCount > 1 ? "s" : ""}
      </p>
    </div>
  );
}
